/**
 * escPosPlainTextDecode.ts — Codifica/decodifica ESC/POS para preview 1:1 con impresión.
 */

import {
  advanceThermalPreviewSection,
  detectThermalPreviewLineKind,
  emitSepLine,
  getTicketMaxWidth,
  prepareThermalPreviewLine,
  shouldLeftAlignThermalPreviewLine,
  withThermalDividerLeadingSpace,
  withThermalPreviewDividerLeadingSpace,
  type ThermalPreviewLine,
  type ThermalPreviewLineAlign,
  type ThermalPreviewSection,
} from "@/features/caja/utils/escposLayout";

const ESC = 0x1b;
const GS = 0x1d;

function concatBytes(...parts: Uint8Array[]): Uint8Array {
  const n = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(n);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

function formatDecodedPreviewText(
  text: string,
  cols: number,
  align: ThermalPreviewLineAlign,
): string {
  if (!text) return "";
  const trimmed = text.trim();
  if (/^_+$/.test(trimmed)) return emitSepLine("_", cols);
  if (align === "left" || shouldLeftAlignThermalPreviewLine(text)) {
    return text.length >= cols ? text.slice(0, cols) : text.padEnd(cols, " ");
  }
  return trimmed.slice(0, cols);
}

function flushDecodedLine(
  out: ThermalPreviewLine[],
  text: string,
  cols: number,
  align: ThermalPreviewLineAlign,
  bold: boolean,
): void {
  const trimmed = text.trim();
  if (!trimmed) {
    out.push({ text: "", align, kind: "blank", bold: false });
    return;
  }
  if (/^_+$/.test(trimmed)) {
    out.push({
      text: emitSepLine("_", cols),
      align: "left",
      kind: "divider",
      bold: false,
    });
    return;
  }
  const kind = detectThermalPreviewLineKind(text, "body");
  out.push({
    text: formatDecodedPreviewText(text, cols, align),
    align: align === "center" ? "center" : "left",
    kind,
    bold,
  });
}

function skipEscPosRaster(buf: Uint8Array, atZero: number): number {
  if (atZero + 5 >= buf.length) return buf.length;
  const xL = buf[atZero + 2];
  const xH = buf[atZero + 3];
  const yL = buf[atZero + 4];
  const yH = buf[atZero + 5];
  const widthBytes = xL + (xH << 8);
  const height = yL + (yH << 8);
  return Math.min(buf.length, atZero + 6 + widthBytes * height);
}

/** Decodifica bytes ESC/POS conservando alineación y negrita. */
export function decodeEscPosToPreviewLines(
  buf: Uint8Array,
  cols: number,
): ThermalPreviewLine[] {
  const out: ThermalPreviewLine[] = [];
  let cur = "";
  let align: ThermalPreviewLineAlign = "left";
  let bold = false;
  let i = 0;

  const pushLine = () => {
    flushDecodedLine(out, cur, cols, align, bold);
    cur = "";
  };

  while (i < buf.length) {
    const b = buf[i];

    if (b === ESC) {
      i += 1;
      if (i >= buf.length) break;
      const cmd = buf[i];
      i += 1;
      if (cmd === 0x40) continue;
      if (cmd === 0x61 && i < buf.length) {
        align = buf[i] === 0x01 ? "center" : "left";
        i += 1;
        continue;
      }
      if (cmd === 0x45 && i < buf.length) {
        bold = buf[i] === 0x01;
        i += 1;
        continue;
      }
      if (cmd === 0x21 && i < buf.length) {
        i += 1;
        continue;
      }
      continue;
    }

    if (b === GS) {
      i += 1;
      if (i >= buf.length) break;
      const gsCmd = buf[i];
      i += 1;
      if (gsCmd === 0x76 && i < buf.length && buf[i] === 0x30) {
        i = skipEscPosRaster(buf, i);
        continue;
      }
      if (gsCmd === 0x56 && i + 1 < buf.length) {
        i += 2;
        continue;
      }
      continue;
    }

    if (b === 0x0a) {
      pushLine();
      i += 1;
      continue;
    }

    if (b === 0x0d) {
      i += 1;
      continue;
    }

    cur += String.fromCharCode(b);
    i += 1;
  }

  if (cur.length > 0) pushLine();
  return withThermalPreviewDividerLeadingSpace(out);
}

export function decodeEscPosToPlainLines(buf: Uint8Array): string[] {
  return decodeEscPosToPreviewLines(buf, 48).map((ln) => ln.text);
}

export type SaleBoldLineFn = (
  line: string,
  section: ThermalPreviewSection,
  headerTextLineIndex: number,
) => boolean;

export interface EncodePlainTextEscPosOpts {
  shouldBold?: SaleBoldLineFn;
  shouldDouble?: (line: string, section: ThermalPreviewSection) => boolean;
  stripLine?: (line: string) => string;
  includeCut?: boolean;
  includeInit?: boolean;
}

/** Codifica líneas planas a ESC/POS (misma alineación/negrita que imprime la APK). */
export function encodePlainTextToEscPos(
  lines: string[],
  cols: number,
  encodeLine: (text: string) => Uint8Array,
  opts: EncodePlainTextEscPosOpts = {},
): Uint8Array {
  const parts: Uint8Array[] = [];
  if (opts.includeInit !== false) {
    parts.push(new Uint8Array([ESC, 0x40]));
  }

  let section: ThermalPreviewSection = "header";
  let alignState: ThermalPreviewLineAlign | null = null;
  let boldState = false;
  let headerTextLineIndex = 0;

  const setAlign = (align: ThermalPreviewLineAlign) => {
    if (alignState === align) return;
    parts.push(new Uint8Array([ESC, 0x61, align === "center" ? 0x01 : 0x00]));
    alignState = align;
  };

  const setBold = (on: boolean) => {
    if (boldState === on) return;
    parts.push(new Uint8Array([ESC, 0x45, on ? 0x01 : 0x00]));
    boldState = on;
  };

  const setDouble = (on: boolean) => {
    parts.push(new Uint8Array([ESC, 0x21, on ? 0x30 : 0x00]));
  };

  for (const rawLine of withThermalDividerLeadingSpace(lines)) {
    const line = opts.stripLine?.(rawLine) ?? rawLine;
    section = advanceThermalPreviewSection(line.trim(), section);
    const meta = prepareThermalPreviewLine(line, cols, section);
    setAlign(meta.align);

    const isBold =
      opts.shouldBold?.(rawLine, section, headerTextLineIndex) ?? false;
    if (section === "header" && line.trim()) {
      headerTextLineIndex += 1;
    }
    setBold(isBold);

    const isDouble = opts.shouldDouble?.(rawLine, section) ?? false;
    if (isDouble) setDouble(true);

    parts.push(encodeLine(line));

    if (isDouble) setDouble(false);
  }

  setBold(false);
  if (opts.includeCut !== false) {
    parts.push(new Uint8Array([GS, 0x56, 0x41, 0x10]));
  }

  return concatBytes(...parts);
}

/** Preview 1:1 desde texto plano (fallback si no hay bytes ESC/POS). */
export function plainTextLinesToPreviewLines(
  lines: string[],
  cols: number,
  shouldBold?: SaleBoldLineFn,
): ThermalPreviewLine[] {
  let section: ThermalPreviewSection = "header";
  let headerTextLineIndex = 0;
  const spaced = withThermalDividerLeadingSpace(lines);
  return spaced.map((line) => {
    section = advanceThermalPreviewSection(line.trim(), section);
    const meta = prepareThermalPreviewLine(line, cols, section);
    const bold = shouldBold?.(line, section, headerTextLineIndex) ?? false;
    if (section === "header" && line.trim()) {
      headerTextLineIndex += 1;
    }
    return { ...meta, bold };
  });
}

export const thermalPaperCols = (paperWidthMm: 58 | 80): number =>
  getTicketMaxWidth(paperWidthMm);
